<script setup lang="ts">
import { storeToRefs } from "pinia"
import { ref, computed, onUnmounted, nextTick } from "vue"
import { Category, EnergySubCategory, PageUrl } from "~/types"
import { useCategoryStore } from '~/stores/useCategoryStore'
import { useFilterStore } from '~/stores/useFilterStore'

import Bars3Icon from '@heroicons/vue/20/solid/Bars3Icon'
import FunnelIcon from '@heroicons/vue/20/solid/FunnelIcon'
import XMarkIcon from '@heroicons/vue/20/solid/XMarkIcon'
import PhoneIcon from '@heroicons/vue/20/solid/PhoneIcon'
import BoltIcon from '@heroicons/vue/20/solid/BoltIcon'
import FireIcon from '@heroicons/vue/20/solid/FireIcon'
import WifiIcon from '@heroicons/vue/20/solid/WifiIcon'
import BookOpenIcon from '@heroicons/vue/20/solid/BookOpenIcon'
import UserGroupIcon from '@heroicons/vue/20/solid/UserGroupIcon'
import LightBulbIcon from '@heroicons/vue/20/solid/LightBulbIcon'
import BriefcaseIcon from '@heroicons/vue/20/solid/BriefcaseIcon'

import IconButton from '~/components/UI/IconButton.vue'
import Filters from '~/components/Filter/Filters.vue'

const props = defineProps<{
  alternative?: boolean
}>()

const { rateList } = storeToRefs(useRateStore())
const router = useRouter()

const navigationMenu = ref()
const filterMenu = ref()

// WCAG 2.1.2 — focus trap per sidebar mobile: il Tab rimane all'interno finché la sidebar è aperta
const hamburgerRef = ref<HTMLButtonElement | null>(null)
const filterButtonRef = ref<HTMLButtonElement | null>(null)
const navSidebarRef = ref<HTMLElement | null>(null)
const filterSidebarRef = ref<HTMLElement | null>(null)

const FOCUSABLE_SELECTOR = 'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
const getFocusable = (el: HTMLElement) =>
  Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(e => e.offsetParent !== null)

const trapNavFocus = (e: KeyboardEvent) => {
  if (e.key !== 'Tab' || !navSidebarRef.value) return
  const els = getFocusable(navSidebarRef.value)
  if (els.length < 2) return
  if (e.shiftKey && document.activeElement === els[0]) { e.preventDefault(); els[els.length - 1].focus() }
  else if (!e.shiftKey && document.activeElement === els[els.length - 1]) { e.preventDefault(); els[0].focus() }
}

const trapFilterFocus = (e: KeyboardEvent) => {
  if (e.key !== 'Tab' || !filterSidebarRef.value) return
  const els = getFocusable(filterSidebarRef.value)
  if (els.length < 2) return
  if (e.shiftKey && document.activeElement === els[0]) { e.preventDefault(); els[els.length - 1].focus() }
  else if (!e.shiftKey && document.activeElement === els[els.length - 1]) { e.preventDefault(); els[0].focus() }
}

const onNavSidebarShow = () => {
  document.addEventListener('keydown', trapNavFocus)
  nextTick(() => navSidebarRef.value && getFocusable(navSidebarRef.value)[0]?.focus())
}
const onNavSidebarHide = () => {
  document.removeEventListener('keydown', trapNavFocus)
  nextTick(() => hamburgerRef.value?.focus())
}

const onFilterSidebarShow = () => {
  document.addEventListener('keydown', trapFilterFocus)
  nextTick(() => filterSidebarRef.value && getFocusable(filterSidebarRef.value)[0]?.focus())
}
const onFilterSidebarHide = () => {
  document.removeEventListener('keydown', trapFilterFocus)
  nextTick(() => filterButtonRef.value?.focus())
}

onUnmounted(() => {
  document.removeEventListener('keydown', trapNavFocus)
  document.removeEventListener('keydown', trapFilterFocus)
})

const { category } = storeToRefs(useCategoryStore())
const { energySubCategory } = storeToRefs(useFilterStore())

const isListPage = computed(() =>
  router.currentRoute.value.name === `category-${Category.ENERGY}` ||
  router.currentRoute.value.name === `category-${Category.INSURANCE}` ||
  router.currentRoute.value.name === `category-${Category.TELCO}`
)

const bgGradient = computed(() =>
  props.alternative ? 'from-[#EE7D22] to-[#F5A840]' : 'from-[#E55092] via-[#E76A7D] via-[#E97A70] to-[#EA806C]'
)

// Colore solido di fallback: il gradiente CSS (background-image) non viene letto dagli
// strumenti di verifica contrasto, che altrimenti risalgono al bianco del <body> (WCAG 1.4.3)
const bgFallbackColor = computed(() => props.alternative ? '#F09A34' : '#E56E7D')

const onNavigateToRoot = () => {
  router.push('/')
}

const onNavigateToCategory = (value: string, subCategory?: string) => {
  if (subCategory) {
    const { setEnergySubCategory } = useFilterStore()
    setEnergySubCategory(subCategory as any)
  }
  router.push(`/categoria/${value}`)
}

const navItems = computed(() => [
  {
    label: 'Luce',
    icon: BoltIcon,
    active: category.value === Category.ENERGY && energySubCategory.value !== EnergySubCategory.GAS,
    click: () => onNavigateToCategory('energia', EnergySubCategory.ELECTRICITY),
    colorClass: 'text-amber-700'
  },
  {
    label: 'Gas',
    icon: FireIcon,
    active: category.value === Category.ENERGY && energySubCategory.value === EnergySubCategory.GAS,
    click: () => onNavigateToCategory('energia', EnergySubCategory.GAS),
    colorClass: 'text-orange-700'
  },
  {
    label: 'Internet',
    icon: WifiIcon,
    active: category.value === Category.TELCO,
    click: () => onNavigateToCategory('telefonia'),
    colorClass: 'text-sky-700'
  },
  {
    label: 'Blog',
    icon: BookOpenIcon,
    active: router.currentRoute.value.path.startsWith('/blog'),
    to: `/${PageUrl.BLOG}`,
    colorClass: 'text-pink-700'
  },
  {
    label: 'Chi Siamo',
    icon: UserGroupIcon,
    active: router.currentRoute.value.path.startsWith('/chi-siamo'),
    to: `/${PageUrl.ABOUT_US}`,
    colorClass: 'text-teal-700'
  },
  {
    label: 'Come Funziona',
    icon: LightBulbIcon,
    active: router.currentRoute.value.path.startsWith('/come-funziona-risparmia-tu'),
    to: `/${PageUrl.HOW_IT_WORKS}`,
    colorClass: 'text-indigo-600'
  },
  {
    label: 'Lavora con noi',
    icon: BriefcaseIcon,
    active: router.currentRoute.value.path.startsWith('/lavora-con-noi'),
    to: `/${PageUrl.WORK_WITH_US}`,
    colorClass: 'text-violet-600'
  }
])
</script>

<template>
  <nav class="fixed left-0 top-0 z-50 h-20 w-full bg-gradient-to-r flex items-center py-0" :class="bgGradient"
    :style="{ backgroundColor: bgFallbackColor }">
    <div class="w-full h-full flex items-center justify-between px-4 xl:container mx-auto xl:px-0">
      <!-- Logo -->
      <NuxtLink to="/" class="relative z-10 flex w-auto items-center justify-center font-semibold normal-case shrink-0"
        aria-label="RisparmiaTu - Vai alla homepage">
        <img class="flex sm:hidden" src="/images/logo-mini.png" width="36" alt="" />
        <img class="hidden sm:flex" src="/images/logo.png" width="180" alt="" />
      </NuxtLink>

      <!-- Desktop Menu (Single Row Center) -->
      <ul class="hidden lg:flex items-center justify-center gap-1.5 xl:gap-3 2xl:gap-5 px-2 shrink-0">
        <li v-for="item in navItems" :key="'desk-' + item.label" class="flex items-center justify-center">
          <NuxtLink
            v-slot="{ href, navigate }"
            v-if="item.to"
            :to="item.to"
            custom
          >
            <a
              :href="href"
              class="flex items-center gap-1.5 py-1.5 px-2.5 xl:px-3 rounded-full text-xs xl:text-sm font-semibold tracking-wide transition-all duration-300 select-none cursor-pointer focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1"
              :class="item.active
                ? 'bg-white shadow-md scale-105 font-bold ' + item.colorClass
                : 'bg-black/25 text-white hover:bg-white/15 hover:scale-105'"
              :aria-current="item.active ? 'page' : undefined"
              @click="navigate"
            >
              <component :is="item.icon" class="h-4 w-4" aria-hidden="true" />
              <span>{{ item.label }}</span>
            </a>
          </NuxtLink>
          <button
            v-else
            class="flex items-center gap-1.5 py-1.5 px-2.5 xl:px-3 rounded-full text-xs xl:text-sm font-semibold tracking-wide transition-all duration-300 select-none cursor-pointer focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1"
            :class="item.active
              ? 'bg-white shadow-md scale-105 font-bold ' + item.colorClass
              : 'bg-black/25 text-white hover:bg-white/15 hover:scale-105'"
            :aria-current="item.active ? 'page' : undefined"
            @click="item.click ? item.click() : null"
          >
            <component :is="item.icon" class="h-4 w-4" aria-hidden="true" />
            <span>{{ item.label }}</span>
          </button>
        </li>
      </ul>

      <!-- Right Actions (Phone & Hamburger) -->
      <div class="flex items-center gap-2 md:gap-4 shrink-0">
        <!-- Clickable Phone Section -->
        <div class="flex items-center gap-2 md:gap-3">
          <!-- Hand-written callout (desktop only, hidden below xl to avoid overflow) -->
          <div class="hidden xl:flex items-center gap-1.5 mr-1 select-none">
            <span class="chiamaci-callout text-yellow-200 text-2xl font-bold tracking-wide rotate-[-3deg] transform">
              Chiamaci ora!
            </span>
            <!-- Cute hand-drawn curved arrow -->
            <svg class="h-5 w-10 text-yellow-200 animate-pulse" viewBox="0 0 40 20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M4 14 C 12 4, 24 4, 34 8" />
              <path d="M28 9 L34 8 L32 3" />
            </svg>
          </div>

          <a href="tel:+390282823842" class="group flex items-center gap-2 rounded-full bg-white/20 border border-white/20 backdrop-blur-md px-3.5 py-2 md:px-5 md:py-2.5 text-sm md:text-base lg:text-base xl:text-lg font-bold text-yellow-200 hover:bg-white hover:text-slate-800 hover:scale-105 transition duration-300 cursor-pointer shadow-lg">
            <PhoneIcon class="h-4 w-4 md:h-5 md:w-5 text-yellow-200 group-hover:text-slate-800 transition-colors" aria-hidden="true" />
            <span class="tracking-wider">02 82 82 38 42</span>
          </a>
        </div>

        <!-- Mobile Filters Button -->
        <button v-if="isListPage" ref="filterButtonRef"
          class="flex md:hidden justify-center items-center h-8 w-8 p-1.5 text-white rounded-full hover:bg-white/10 cursor-pointer transition focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1"
          type="button" aria-label="Apri filtri" @click="filterMenu = true">
          <FunnelIcon class="w-full h-full" aria-hidden="true" />
        </button>
        <Sidebar v-model:visible="filterMenu" position="bottom" :block-scroll="true" :show-close-icon="false"
          @show="onFilterSidebarShow" @hide="onFilterSidebarHide">
          <div ref="filterSidebarRef" class="flex flex-col gap-8 scrollable-container p-8 max-h-[80vh] overflow-scroll">
            <div class="flex justify-between items-center gap-4">
              <span class="text-sm text-slate-500 font-bold uppercase">
                Filtri
              </span>
              <IconButton variant="transparent" size="small" aria-label="Chiudi filtri" @click="filterMenu = false">
                <XMarkIcon class="cursor-pointer" aria-hidden="true" />
              </IconButton>
            </div>
            <Filters :options="rateList" @submit="filterMenu = false" />
          </div>
        </Sidebar>

        <!-- Mobile Sidebar Hamburger (visible below lg since menu is hidden there) -->
        <button ref="hamburgerRef"
          class="flex lg:hidden justify-center items-center h-8 w-8 p-1.5 text-white rounded-full hover:bg-white/10 cursor-pointer transition focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1"
          type="button" aria-label="Apri menu di navigazione" :aria-expanded="!!navigationMenu"
          @click="navigationMenu = true">
          <Bars3Icon class="w-full h-full" aria-hidden="true" />
        </button>

        <!-- Mobile Navigation Sidebar -->
        <Sidebar v-model:visible="navigationMenu" position="top" :block-scroll="true" :show-close-icon="false"
          @show="onNavSidebarShow" @hide="onNavSidebarHide">
          <div ref="navSidebarRef" class="w-full flex flex-col gap-6 pt-10 pb-6 px-6 h-auto bg-gradient-to-r border-b border-white/15" :class="bgGradient" :style="{ backgroundColor: bgFallbackColor }">
            <div class="flex justify-between items-center">
              <span class="text-xs text-white bg-black/25 px-2 py-1 rounded font-bold uppercase tracking-wider">Menu</span>
              <IconButton variant="transparent" size="small" aria-label="Chiudi menu" @click="navigationMenu = false">
                <XMarkIcon class="text-white hover:text-white/80 cursor-pointer" aria-hidden="true" />
              </IconButton>
            </div>
            <ul class="flex flex-col gap-3 font-semibold text-white">
              <li v-for="item in navItems" :key="'sidebar-' + item.label">
                <NuxtLink
                  v-slot="{ href, navigate }"
                  v-if="item.to"
                  :to="item.to"
                  custom
                >
                  <a
                    :href="href"
                    class="flex items-center gap-3 w-full py-2.5 px-4 rounded-xl transition-all duration-200 text-left cursor-pointer focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1"
                    :class="item.active ? 'bg-white font-bold shadow-md ' + item.colorClass : 'bg-black/25 text-white hover:bg-white/10'"
                    :aria-current="item.active ? 'page' : undefined"
                    @click="(e) => { navigate(e); navigationMenu = false; }"
                  >
                    <component :is="item.icon" class="h-5 w-5" aria-hidden="true" />
                    <span class="text-base">{{ item.label }}</span>
                  </a>
                </NuxtLink>
                <button
                  v-else
                  class="flex items-center gap-3 w-full py-2.5 px-4 rounded-xl transition-all duration-200 text-left cursor-pointer focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1"
                  :class="item.active ? 'bg-white font-bold shadow-md ' + item.colorClass : 'bg-black/25 text-white hover:bg-white/10'"
                  :aria-current="item.active ? 'page' : undefined"
                  @click="() => { if (item.click) item.click(); navigationMenu = false; }"
                >
                  <component :is="item.icon" class="h-5 w-5" aria-hidden="true" />
                  <span class="text-base">{{ item.label }}</span>
                </button>
              </li>
            </ul>
          </div>
        </Sidebar>
      </div>
    </div>
  </nav>
</template>

<style lang="scss" scoped>
/* Font Caveat servito in locale dal modulo @nuxtjs/google-fonts (vedi nuxt.config.ts),
   non più importato da fonts.googleapis.com, che riceveva l'IP del visitatore prima del consenso */
.chiamaci-callout {
  font-family: 'Caveat', cursive;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
}

nav {
  @media (min-width: 768px) {

    &:before,
    &:after {
      content: '';
      position: absolute;
      top: 0;
      height: 80px;
      width: 80px;
      background-size: 80%;
      background-repeat: no-repeat;
      background-position: center center;
      z-index: 9;
      opacity: 0.9;
    }

    &:before {
      left: -34px;
      background-image: url('/images/triangle_left.svg');
    }

    &:after {
      right: -34px;
      background-image: url('/images/triangle_right.svg');
    }
  }
}

.scrollbar-none::-webkit-scrollbar {
  display: none;
}
.scrollbar-none {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
